import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  getSkills,
  getSkillCategories,
  createSkill,
  updateSkill,
  deleteSkill,
  Skill,
  CreateSkillRequest,
  UpdateSkillRequest,
} from '../services/userService';

export default function SkillsPage() {
  const { t } = useTranslation();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [isNewCategory, setIsNewCategory] = useState(false);
  
  const [form, setForm] = useState<CreateSkillRequest>({
    name: '',
    description: '',
    category: '',
    icon: '',
  });

  useEffect(() => {
    loadData();
  }, [filterCategory]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [skillsData, categoriesData] = await Promise.all([
        getSkills(filterCategory || undefined),
        getSkillCategories(),
      ]);
      setSkills(skillsData);
      setCategories(categoriesData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load skills');
      console.error('Error loading skills:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingSkill(null);
    setIsNewCategory(categories.length === 0);
    setForm({
      name: '',
      description: '',
      category: '',
      icon: '',
    });
    setDialogOpen(true);
  };

  const handleEdit = (skill: Skill) => {
    setEditingSkill(skill);
    setIsNewCategory(false);
    setForm({
      name: skill.name,
      description: skill.description || '',
      category: skill.category,
      icon: skill.icon || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingSkill) {
        await updateSkill(editingSkill.id, form as UpdateSkillRequest);
      } else {
        await createSkill(form);
      }
      setDialogOpen(false);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save skill');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('skills.confirmDelete', 'Are you sure you want to delete this skill?'))) {
      return;
    }
    
    try {
      await deleteSkill(id);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete skill');
    }
  };

  // Group skills by category
  const groupedSkills = skills.reduce((acc, skill) => {
    if (!acc[skill.category]) {
      acc[skill.category] = [];
    }
    acc[skill.category].push(skill);
    return acc;
  }, {} as Record<string, Skill[]>);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {t('skills.title', 'Skills')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          {t('skills.addSkill', 'Add Skill')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          select
          label={t('skills.filterByCategory', 'Filter by Category')}
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">
            <em>{t('skills.allCategories', 'All Categories')}</em>
          </MenuItem>
          {categories.map((category) => (
            <MenuItem key={category} value={category}>
              {category}
            </MenuItem>
          ))}
        </TextField>
      </Paper>

      {/* Skills Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('skills.skill', 'Skill')}</TableCell>
              <TableCell>{t('skills.description', 'Description')}</TableCell>
              <TableCell>{t('skills.category', 'Category')}</TableCell>
              <TableCell>{t('skills.userCount', 'Users')}</TableCell>
              <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {skills.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="textSecondary">
                    {t('skills.noSkills', 'No skills defined')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              skills.map((skill) => (
                <TableRow key={skill.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {skill.icon && <span style={{ fontSize: '1.5rem' }}>{skill.icon}</span>}
                      <Typography>{skill.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{skill.description || '-'}</TableCell>
                  <TableCell>
                    <Chip label={skill.category} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={skill.userCount} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(skill)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(skill.id)}
                      disabled={skill.userCount > 0}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSkill
            ? t('skills.editSkill', 'Edit Skill')
            : t('skills.addSkill', 'Add Skill')}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label={t('skills.name', 'Name')}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              required
              placeholder="React, Python, Project Management, etc."
            />
            <TextField
              label={t('skills.description', 'Description')}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Brief description of this skill..."
            />
            
            {/* Category with toggle between select and text input */}
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="body2" color="text.secondary">
                  {t('skills.category', 'Category')}
                </Typography>
                {categories.length > 0 && (
                  <Button
                    size="small"
                    onClick={() => setIsNewCategory(!isNewCategory)}
                  >
                    {isNewCategory ? 'Select Existing' : 'Create New'}
                  </Button>
                )}
              </Box>
              
              {isNewCategory || categories.length === 0 ? (
                <TextField
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  fullWidth
                  required
                  placeholder="Frontend Development, Backend Development, etc."
                  helperText={t('skills.categoryHelp', 'Enter a new category name')}
                />
              ) : (
                <TextField
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  fullWidth
                  required
                  select
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
            
            {/* Icon picker */}
            <Box>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {t('skills.icon', 'Icon (Emoji)')}
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                {['💻', '⚛️', '🐍', '☕', '📊', '🎨', '🔧', '📱', '🌐', '🗄️', '🔒', '📈', '🚀', '⚙️', '📝', '🎯'].map((emoji) => (
                  <Button
                    key={emoji}
                    variant={form.icon === emoji ? 'contained' : 'outlined'}
                    onClick={() => setForm({ ...form, icon: emoji })}
                    sx={{ minWidth: 48, fontSize: '1.5rem', p: 1 }}
                  >
                    {emoji}
                  </Button>
                ))}
              </Box>
              <TextField
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                fullWidth
                placeholder="Or paste any emoji"
                helperText={t('skills.iconHelp', 'Select from above or paste your own emoji')}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleSave} variant="contained" disabled={!form.name || !form.category}>
            {t('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
